import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { useCText, useCValue } from "../lib/cms";

export default function Contact() {
  const { t } = useI18n();
  const title = useCText("contact.title", "contact.title");
  const lead = useCText("contact.lead", "contact.lead");
  const email = useCValue("contact.email", "");
  const whatsapp = useCValue("contact.whatsapp", "");
  const address = useCValue("contact.address", "");
  const instagram = useCValue("social.instagram", "");
  const discogs = useCValue("social.discogs", "");
  const waDigits = whatsapp.replace(/[^\d]/g, "").replace(/^00/, "");
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
      toast.success(t("contact.success"));
    } catch {
      toast.error(t("contact.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-10 md:pt-14" data-testid="contact-page">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <h1 className="text-2xl md:text-4xl font-medium leading-tight mb-5">
            {title}
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed mb-6 max-w-md">
            {lead}
          </p>
          <div className="text-sm text-muted-foreground space-y-1.5" data-testid="contact-details">
            {email && <p><a href={`mailto:${email}`} className="hover:text-foreground underline underline-offset-4">{email}</a></p>}
            {whatsapp && (
              <p>
                WhatsApp:{" "}
                {waDigits.length >= 8 ? (
                  <a href={`https://wa.me/${waDigits}`} target="_blank" rel="noreferrer" className="hover:text-foreground underline underline-offset-4">{whatsapp}</a>
                ) : whatsapp}
              </p>
            )}
            {address && <p className="whitespace-pre-line">{address}</p>}
            <p className="flex gap-4 pt-1">
              {instagram && <a href={instagram} target="_blank" rel="noreferrer" className="hover:text-foreground underline underline-offset-4">Instagram ↗</a>}
              {discogs && <a href={discogs} target="_blank" rel="noreferrer" className="hover:text-foreground underline underline-offset-4">Discogs ↗</a>}
            </p>
          </div>
        </div>

        <div className="md:col-span-7">
          <form onSubmit={submit} className="flex flex-col gap-4 max-w-xl" data-testid="contact-form">
            <Field label={t("contact.name")} value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="contact-name" />
            <Field label={t("contact.email")} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="contact-email" />
            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-muted-foreground">{t("contact.message")}</label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={6}
                required
                className="w-full bg-background border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground"
                data-testid="contact-message"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="self-start bg-foreground text-background px-6 py-2.5 text-sm hover:bg-[hsl(var(--accent))] transition-colors disabled:opacity-50"
              data-testid="contact-submit"
            >
              {loading ? t("contact.sending") : t("contact.send")}
            </button>
            {sent && (
              <p className="text-xs text-muted-foreground" data-testid="contact-success">
                {t("contact.success")}
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs text-muted-foreground">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full bg-background border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground"
        data-testid={testid}
      />
    </div>
  );
}
